import { useState } from 'react'
import type { JSX } from 'react'

import {
  Flex,
  Link as PopcornLink,
  PageHeader,
  Separator,
  Text,
  ZapIcon,
} from '@buffer-mono/popcorn'

import { FeedbackWidget } from '~publish/components/FeedbackWidget'
import { ViewOnlyBadge } from '~publish/components/ViewOnlyBadge'
import { ViewOnlyPublishCoachMark } from '~publish/components/ViewOnlyCoachMarks'
import { useIsFreeUser } from '~publish/contexts/AccountContext'

import type { PostType, SortDirection, TopPostsSortBy } from '../graphqlEnums'
import { ExportConfigDialog } from '../components/ExportConfigDialog'
import { ExportDropdown } from '../components/ExportDropdown'
import { InsightsUpgradeDialog } from '../components/InsightsUpgradeDialog'
import { SyncIcon } from '../components/SyncIcon'
import { useInsightsFilterInput } from '../context'
import {
  useDownloadSnapshots,
  useInsightsExportAction,
  useInsightsExportUpgrade,
} from '../hooks'
import {
  InsightsActionsMenu,
  InsightsDeveloperActionsMenu,
} from '../components/InsightsActionsMenu'
import type { DeveloperDataProps } from '../components/InsightsActionsMenu'

import { getViewOnlyInsightsCopy } from '../viewOnlyCopy'

import styles from './InsightsChannel.module.css'

interface InsightsHeaderActionsProps {
  /** Renders the "View only" badge in the action cluster. */
  isViewOnly?: boolean
  /** Whether to render the "Go to Analyze" link. */
  shouldShowAnalyzeLink: boolean
  canExport: boolean
  /** Pre-built Analyze URL (requires channel service from parent query). */
  analyzeUrl?: string
  /** Top-posts total count, used to decide whether to generate a PDF. */
  postCount?: number
  /** From the channel analytics query. */
  channelLastUpdatedAt?: string | null
  postsLastUpdatedAt?: string | null
  /** When provided, shows developer-only items. Omit to hide them. */
  developerData?: DeveloperDataProps
  /**
   * The page's active Top Posts sort, carried into the export so the exported
   * posts are ordered the way the user sees them (CORE-6039).
   */
  postSort?: { field: TopPostsSortBy; direction: SortDirection }
  /**
   * The Top Posts table's visible columns (in display order), carried into the
   * export so the exported posts tables show the same metric columns.
   */
  postColumns?: string[]
  /**
   * The page's active post type selection, already narrowed to the formats the
   * channel's service can produce. Carried into the export so the file matches
   * the page. @see supportedPostTypesForService
   */
  postTypes?: readonly PostType[]
}

export function InsightsHeaderActions({
  isViewOnly = false,
  shouldShowAnalyzeLink,
  canExport,
  analyzeUrl,
  postCount,
  channelLastUpdatedAt,
  postsLastUpdatedAt,
  developerData,
  postSort,
  postColumns,
  postTypes,
}: InsightsHeaderActionsProps): JSX.Element {
  const isFreeUser = useIsFreeUser()
  // Primary-date filter scoped to this channel — never flips to comparison dates.
  const exportFilter = useInsightsFilterInput()

  // Single upgrade-dialog state — outlives CSS layout switches.
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false)
  // PDF export configuration dialog (sections + logo). Owned here so it
  // survives the wide/narrow layout switch, like the upgrade dialog.
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false)
  const { handleExportBlocked } = useInsightsExportUpgrade()

  const onUpgradeRequired = (): void => setIsUpgradeDialogOpen(true)

  const {
    showExport,
    showUpgradePath,
    isExporting,
    isLongRunning,
    handleSelect,
    exportWithConfig,
  } = useInsightsExportAction(
    {
      organizationId: exportFilter.organizationId,
      channelIds: exportFilter.channelIds ?? [],
      canExport,
      startDate: exportFilter.startDate,
      endDate: exportFilter.endDate,
      tags: exportFilter.tags ?? undefined,
      // The prop, not `exportFilter.postTypes`. Both are in scope and only this
      // one is narrowed to the formats the channel's service can produce —
      // reading the filter input here would export a format the service never
      // stores, with no control on screen to clear it.
      postTypes,
      postCount,
      postSort,
      postColumns,
    },
    { onUpgradeRequired, onConfigurePdf: () => setIsConfigDialogOpen(true) },
  )

  // Separate from handleSelect: the snapshot download bypasses the export
  // upgrade dialog (it's not paywalled).
  const {
    downloadSnapshots,
    isPreparing: isPreparingSnapshots,
    canDownload: canDownloadSnapshots,
  } = useDownloadSnapshots()

  // Close InsightsHeaderActions' dialog (not the hook's internal one) then navigate.
  const handleUpgradeClick = (): void => {
    setIsUpgradeDialogOpen(false)
    handleExportBlocked()
  }

  return (
    <>
      <Flex direction="row" align="center" gap="xs">
        {/* Wide-screen layout — hidden when page-header container < 650 px */}
        <div className={styles.expandedActions}>
          <PageHeader.ActionGroup className={styles.primaryActions}>
            {shouldShowAnalyzeLink && analyzeUrl && (
              <>
                <div
                  className={styles.analyzeCtaFull}
                  data-testid="insights-analyze-cta-full"
                >
                  <Text color="subtle" size="sm">
                    Looking for more metrics and reports?{' '}
                    <PopcornLink href={analyzeUrl} external>
                      {isFreeUser && <ZapIcon size="xsmall" />} Go to Analyze
                    </PopcornLink>
                  </Text>
                </div>
                <div
                  className={styles.analyzeCtaCompact}
                  data-testid="insights-analyze-cta-compact"
                >
                  <PopcornLink href={analyzeUrl} external>
                    Go to Analyze
                  </PopcornLink>
                </div>
                {/* Divides the Analyze link from the controls beside it. The
                    only divider the header keeps. */}
                <Separator
                  orientation="vertical"
                  decorative
                  className={styles.separator}
                />
              </>
            )}
            {isViewOnly && (
              <div className={styles.viewOnlyBadge}>
                <ViewOnlyPublishCoachMark
                  copy={getViewOnlyInsightsCopy({
                    surface: 'channel',
                    canExport,
                  })}
                >
                  <ViewOnlyBadge />
                </ViewOnlyPublishCoachMark>
              </div>
            )}
            {/* Feedback sits just left of Sync, in the action cluster. */}
            <div className={styles.feedbackAction}>
              <FeedbackWidget id="insights-1" source="insights" />
            </div>
            <SyncIcon
              channelLastUpdatedAt={channelLastUpdatedAt}
              postsLastUpdatedAt={postsLastUpdatedAt}
            />
            {/* Internal dev tool — hidden from customers, gated by cx-show-app-data Split flag */}
            {developerData && (
              <InsightsDeveloperActionsMenu developerData={developerData} />
            )}
          </PageHeader.ActionGroup>
          {/* Export is its own group, so it sits a step further from the
              cluster. Guarded because ExportDropdown renders nothing without
              `showExport`, and an empty group would still hold the wider gap
              open. */}
          {showExport && (
            <PageHeader.ActionGroup>
              <ExportDropdown
                showExport={showExport}
                showUpgradePath={showUpgradePath}
                isExporting={isExporting}
                isLongRunning={isLongRunning}
                onSelect={handleSelect}
                onDownloadSnapshots={downloadSnapshots}
                canDownloadSnapshots={canDownloadSnapshots}
                isPreparingSnapshots={isPreparingSnapshots}
              />
            </PageHeader.ActionGroup>
          )}
        </div>

        {/* Narrow-screen layout — shown when page-header container < 650 px */}
        <div className={styles.collapsedActionsMenu}>
          <InsightsActionsMenu
            analyzeUrl={shouldShowAnalyzeLink ? analyzeUrl : undefined}
            channelLastUpdatedAt={channelLastUpdatedAt}
            postsLastUpdatedAt={postsLastUpdatedAt}
            showExport={showExport}
            showUpgradePath={showUpgradePath}
            isExporting={isExporting}
            isLongRunning={isLongRunning}
            onExportSelect={handleSelect}
            onDownloadSnapshots={downloadSnapshots}
            canDownloadSnapshots={canDownloadSnapshots}
            isPreparingSnapshots={isPreparingSnapshots}
            developerData={developerData}
          />
        </div>
      </Flex>

      {/* Single upgrade dialog — owned here so it survives CSS layout switches */}
      {showUpgradePath && (
        <InsightsUpgradeDialog
          flow="export"
          open={isUpgradeDialogOpen}
          onOpenChange={setIsUpgradeDialogOpen}
          onUpgradeClick={handleUpgradeClick}
        />
      )}

      <ExportConfigDialog
        open={isConfigDialogOpen}
        onOpenChange={setIsConfigDialogOpen}
        variant="single"
        onConfirm={exportWithConfig}
      />
    </>
  )
}
